import React, { useEffect, useMemo, useState } from 'react';

export default function SharePage({ requestApi }) {
  const shareId = useMemo(() => {
    const p = window.location.pathname || '';
    const m = p.match(/^\/share\/([^/?#]+)$/);
    if (m) return decodeURIComponent(m[1]);
    const sp = new URLSearchParams(window.location.search || '');
    return sp.get('shareId') || '';
  }, []);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [share, setShare] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function run() {
      if (!shareId) {
        setErr('缺少分享ID');
        setLoading(false);
        return;
      }
      try {
        const r = await requestApi(`/share/${shareId}`);
        if (!mounted) return;
        if (!r?.success || !r?.share) {
          setErr(r?.error || '分享已失效或不存在');
        } else {
          setShare(r.share);
        }
      } catch (_) {
        if (!mounted) return;
        setErr('网络异常，请稍后再试');
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    }
    run();
    return () => {
      mounted = false;
    };
  }, [requestApi, shareId]);

  const data = share?.data || {};

  return (
    <div className="panel">
      <h2 className="panel-title">分享内容</h2>
      <p className="panel-subtitle">仅展示分享者选择公开的结果快照（文化体验向）</p>

      {loading ? <p className="panel-subtitle">加载中…</p> : null}
      {err ? <p className="error-text">{err}</p> : null}

      {!loading && !err && share?.type === 'yunxiang' ? (
        <div className="yunxiang-result">
          <div className="yunxiang-result-card">
            <h3 className="yunxiang-result-title">{data.title}</h3>
            <p className="yunxiang-result-poetic">{data.poeticLine}</p>
            <div className="yunxiang-result-bless">{data.blessing}</div>
            <div className="yunxiang-result-meta">
              <span>场景：{data.sceneLabel}</span>
              <span>主题：{data.themeLabel}</span>
              {data.solarTerm ? <span>节气：{data.solarTerm}</span> : null}
            </div>
            <p className="yunxiang-result-lunar">{data.lunarStr}</p>
            <div className="yunxiang-result-section">
              <strong>行动建议</strong>
              <p>{data.guidance}</p>
            </div>
            <div className="yunxiang-result-mindfulness">
              <span>正念</span>
              {data.mindfulness}
            </div>
          </div>
          <p className="panel-subtitle" style={{ marginBottom: 0 }}>
            云端问道 · 文化体验非宗教法事 · 不宣称灵验、改运或现实效力
          </p>
        </div>
      ) : null}

      {!loading && !err && share?.type === 'bazi' ? (
        <div className="result-card">
          <div className="result-card-title">🧬 八字批解快照</div>
          <div className="result-card-content">
            <p className="bazi-pro-line">
              <span className="bazi-pro-label">四柱</span>
              {data?.bazi?.year} {data?.bazi?.month} {data?.bazi?.day} {data?.bazi?.hour}
            </p>
            <p className="bazi-pro-line">
              <span className="bazi-pro-label">日主</span>
              {data?.dayMaster?.gan}{data?.dayMaster?.zhi}（{data?.dayMaster?.wuxing}）{data?.dayMaster?.strength}
              {typeof data?.dayMaster?.score === 'number' ? ` · ${data.dayMaster.score}分` : ''}
            </p>
            <p className="bazi-pro-line">{data?.summary}</p>
            {Array.isArray(data?.suggestions) && data.suggestions.length > 0 ? (
              <div className="yunxiang-result-section">
                <strong>建议摘录</strong>
                <ul className="bazi-pro-shishen-list">
                  {data.suggestions.slice(0, 6).map((s, i) => (
                    <li key={i}>{s}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {data?.disclaimer ? <p className="bazi-pro-meta">{data.disclaimer}</p> : null}
          </div>
        </div>
      ) : null}

      {!loading && !err && share?.type === 'daily' ? (
        <div className="result-card">
          <div className="result-card-title">📆 每日运势快照</div>
          <div className="result-card-content">
            <p className="bazi-pro-line"><span className="bazi-pro-label">日期</span>{data.date}</p>
            <p className="bazi-pro-line"><span className="bazi-pro-label">综合</span>{data.overall}分</p>
            <p className="bazi-pro-line"><span className="bazi-pro-label">幸运</span>{data.luckyColor} · {data.luckyDirection} · {(data.luckyNumbers || []).join('、')}</p>
            <div className="yunxiang-result-bless" style={{ whiteSpace: 'pre-wrap' }}>{data.horoscope}</div>
          </div>
        </div>
      ) : null}

      {!loading && !err && share?.type === 'dream' ? (
        <div className="result-card">
          <div className="result-card-title">🌙 周公解梦快照</div>
          <div className="result-card-content">
            <p className="bazi-pro-line"><span className="bazi-pro-label">梦境</span>{data.dream}</p>
            <p className="bazi-pro-line"><span className="bazi-pro-label">情绪</span>{data.emotion}</p>
            <div className="yunxiang-result-bless" style={{ whiteSpace: 'pre-wrap' }}>{data.analysis}</div>
            {data.disclaimer ? <p className="bazi-pro-meta">{data.disclaimer}</p> : null}
          </div>
        </div>
      ) : null}

      {!loading && !err && share?.type === 'name' ? (
        <div className="result-card">
          <div className="result-card-title">👶 宝宝取名快照</div>
          <div className="result-card-content">
            <p className="bazi-pro-line"><span className="bazi-pro-label">姓氏</span>{data.surname}（{data.gender}）</p>
            {Array.isArray(data.names) ? data.names.map((n, i) => (
              <div key={i} className="yunxiang-result-section" style={{ marginBottom: 10 }}>
                <strong>{n.fullName}</strong>
                <p style={{ margin: 0 }}>{n.meaning}</p>
              </div>
            )) : null}
            {data.disclaimer ? <p className="bazi-pro-meta">{data.disclaimer}</p> : null}
          </div>
        </div>
      ) : null}

      {!loading && !err && share?.type === 'date' ? (
        <div className="result-card">
          <div className="result-card-title">📅 良辰吉日快照</div>
          <div className="result-card-content">
            {data.ruleSummary ? <p className="bazi-pro-line">{data.ruleSummary}</p> : null}
            {Array.isArray(data.dates) ? data.dates.map((d, i) => (
              <div key={i} className="yunxiang-history-item" style={{ cursor: 'default' }}>
                <span className="yunxiang-history-title">{d.date} · {d.lunar} · {typeof d.score === 'number' ? `${d.score}分` : ''}</span>
                <span className="yunxiang-history-meta">宜：{d.yi} ｜ 忌：{d.ji}</span>
              </div>
            )) : null}
            {data.analysis ? <div className="yunxiang-result-bless" style={{ whiteSpace: 'pre-wrap', marginTop: 12 }}>{data.analysis}</div> : null}
            {data.disclaimer ? <p className="bazi-pro-meta" style={{ marginTop: 12 }}>{data.disclaimer}</p> : null}
          </div>
        </div>
      ) : null}

      {!loading && !err && share?.type === 'mbti' ? (
        <div className="result-card">
          <div className="result-card-title">🎯 性格测试快照</div>
          <div className="result-card-content">
            <p className="bazi-pro-line"><span className="bazi-pro-label">类型</span>{data.mbtiType}</p>
            {Array.isArray(data.traits) && data.traits.length > 0 ? (
              <div className="suggestions-row">
                {data.traits.map((t, i) => <span key={i} className="suggestion-tag">{t}</span>)}
              </div>
            ) : null}
            {Array.isArray(data.careers) && data.careers.length > 0 ? (
              <div className="yunxiang-result-section">
                <strong>职业方向</strong>
                <p style={{ margin: 0 }}>{data.careers.join('、')}</p>
              </div>
            ) : null}
            <div className="yunxiang-result-bless" style={{ whiteSpace: 'pre-wrap' }}>{data.analysis}</div>
          </div>
        </div>
      ) : null}

      {!loading && !err && share?.type === 'aura' ? (
        <div className="result-card">
          <div className="result-card-title">🫧 气场扫描快照</div>
          <div className="result-card-content">
            <p className="bazi-pro-line"><span className="bazi-pro-label">主导色</span>{data.dominantColor || '—'}</p>
            {typeof data.energyLevel === 'number' ? <p className="bazi-pro-line"><span className="bazi-pro-label">节律指数</span>{data.energyLevel}%</p> : null}
            {typeof data.stressTendency === 'number' ? <p className="bazi-pro-line"><span className="bazi-pro-label">压力倾向</span>{data.stressTendency}</p> : null}
            {data.colorMeaning ? (
              <div className="yunxiang-result-section">
                <strong>象征含义</strong>
                <p style={{ margin: 0 }}>{data.colorMeaning}</p>
              </div>
            ) : null}
            {data.advice ? (
              <div className="yunxiang-result-section">
                <strong>小贴士</strong>
                <p style={{ margin: 0 }}>{data.advice}</p>
              </div>
            ) : null}
            {data.analysis ? <div className="yunxiang-result-bless" style={{ whiteSpace: 'pre-wrap' }}>{data.analysis}</div> : null}
            {data.disclaimer ? <p className="bazi-pro-meta" style={{ marginTop: 12 }}>{data.disclaimer}</p> : null}
          </div>
        </div>
      ) : null}

      {!loading && !err && share?.type === 'face' ? (
        <div className="result-card">
          <div className="result-card-title">👤 面相探微快照</div>
          <div className="result-card-content">
            {typeof data.score === 'number' ? <p className="bazi-pro-line"><span className="bazi-pro-label">综合</span>{data.score}分</p> : null}
            {Array.isArray(data.features) && data.features.length > 0 ? (
              <div className="yunxiang-result-section">
                <strong>五官倾向（摘录）</strong>
                <ul className="bazi-pro-shishen-list">
                  {data.features.slice(0, 8).map((f, i) => (
                    <li key={i}>{f.name}：{f.meaning}{typeof f.score === 'number' ? `（${f.score}分）` : ''}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {Array.isArray(data.palaceBrief) && data.palaceBrief.length > 0 ? (
              <div className="yunxiang-result-section">
                <strong>宫位提示（摘录）</strong>
                <ul className="bazi-pro-shishen-list">
                  {data.palaceBrief.slice(0, 6).map((p, i) => (
                    <li key={i}><strong>{p.tag}</strong> {p.text}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {data.analysis ? <div className="yunxiang-result-bless" style={{ whiteSpace: 'pre-wrap' }}>{data.analysis}</div> : null}
            {data.disclaimer ? <p className="bazi-pro-meta" style={{ marginTop: 12 }}>{data.disclaimer}</p> : null}
          </div>
        </div>
      ) : null}

      {!loading && !err && share?.type === 'fengshui' ? (
        <div className="result-card">
          <div className="result-card-title">🏠 风水顾问快照</div>
          <div className="result-card-content">
            {typeof data.score === 'number' ? <p className="bazi-pro-line"><span className="bazi-pro-label">规则参考分</span>{data.score}分</p> : null}
            {data.wuxing ? (
              <p className="bazi-pro-line">
                <span className="bazi-pro-label">朝向</span>
                楼层五行：{data.wuxing.floor || '—'} · 房屋：{data.wuxing.mainDirection || '—'} · 入户门：{data.wuxing.doorDirection || '—'}
              </p>
            ) : null}
            {Array.isArray(data.layoutRuleLines) && data.layoutRuleLines.length > 0 ? (
              <div className="yunxiang-result-section">
                <strong>布局提示（摘录）</strong>
                <ul className="bazi-pro-shishen-list">
                  {data.layoutRuleLines.slice(0, 10).map((l, i) => <li key={i}>{l}</li>)}
                </ul>
              </div>
            ) : null}
            {Array.isArray(data.tips) && data.tips.length > 0 ? (
              <div className="yunxiang-result-section">
                <strong>建议（摘录）</strong>
                <ul className="bazi-pro-shishen-list">
                  {data.tips.slice(0, 6).map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
            ) : null}
            {data.report ? <div className="yunxiang-result-bless" style={{ whiteSpace: 'pre-wrap' }}>{data.report}</div> : null}
            {data.disclaimer ? <p className="bazi-pro-meta" style={{ marginTop: 12 }}>{data.disclaimer}</p> : null}
          </div>
        </div>
      ) : null}

      {!loading && !err && share?.type === 'matchmaking' ? (
        <div className="result-card">
          <div className="result-card-title">💞 缘分合盘报告快照</div>
          <div className="result-card-content">
            {typeof data.score === 'number' ? (
              <p className="bazi-pro-line">
                <span className="bazi-pro-label">合盘指数</span>
                {data.score}/100
              </p>
            ) : null}
            {data.summary ? <p className="bazi-pro-line">{data.summary}</p> : null}

            {Array.isArray(data.strengths) && data.strengths.length > 0 ? (
              <div className="yunxiang-result-section">
                <strong>互补优势（摘录）</strong>
                <ul className="bazi-pro-shishen-list">
                  {data.strengths.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            ) : null}

            {Array.isArray(data.cautions) && data.cautions.length > 0 ? (
              <div className="yunxiang-result-section">
                <strong>潜在摩擦点（摘录）</strong>
                <ul className="bazi-pro-shishen-list">
                  {data.cautions.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            ) : null}

            {Array.isArray(data.tips) && data.tips.length > 0 ? (
              <div className="yunxiang-result-section">
                <strong>破冰沟通锦囊（摘录）</strong>
                <ul className="bazi-pro-shishen-list">
                  {data.tips.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
            ) : null}

            {data.fullReport ? (
              <div className="yunxiang-result-bless" style={{ whiteSpace: 'pre-wrap', marginTop: 12 }}>
                {data.fullReport}
              </div>
            ) : null}

            {data.disclaimer ? (
              <p className="bazi-pro-meta" style={{ marginTop: 12 }}>{data.disclaimer}</p>
            ) : null}
          </div>
        </div>
      ) : null}

      {!loading && !err && (share?.type === 'chat' || share?.type === 'spirit') ? (
        <div className="result-card">
          <div className="result-card-title">{share?.type === 'chat' ? '🫶 暖愈心愈快照' : '🐉 灵兽传音快照'}</div>
          <div className="result-card-content">
            {data.userText ? <p className="bazi-pro-line"><span className="bazi-pro-label">你</span>{data.userText}</p> : null}
            <div className="yunxiang-result-bless" style={{ whiteSpace: 'pre-wrap' }}>{data.reply}</div>
            <p className="bazi-pro-meta" style={{ marginTop: 12 }}>提示：此为对话片段快照，仅作分享与记录，不构成医疗/法律/决策建议。</p>
          </div>
        </div>
      ) : null}

      {!loading && !err && share?.type === 'talisman' ? (
        <div className="result-card">
          <div className="result-card-title">🔮 赛博灵符快照</div>
          <div className="result-card-content">
            <p className="bazi-pro-line"><span className="bazi-pro-label">主题</span>{data.talismanTitle || data.title || '—'}</p>
            {data.blessing ? <div className="yunxiang-result-bless" style={{ whiteSpace: 'pre-wrap' }}>{data.blessing}</div> : null}
            <p className="bazi-pro-meta" style={{ marginTop: 12 }}>{data.disclaimer || '本内容为文化装饰与数字壁纸用途，不具有现实灵验或任何保障效力。'}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

